import React, { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useChangeSelectedCompanyStore } from "@/stores/useChangeSelectedCompanyStore";
import { useDashboardStore } from "@/stores/useDashboardStore";
import { useDrawerStore } from "@/stores/useDrawerStore";

import { useDashboardContext } from "../../../../context/dashoard/DashboardProvider";
import { useMediaQueries } from "../../../../shared";
import CardWrapper from "../../CardWrapper/CardWrapper";
import { useStyles as dashboardStyles } from "../../styles";
import { CONNECTIVITY_TABS } from "../../types";
import useChartElements from "../hooks/useChartElements";
import useDeviceStatus from "./hooks/useDeviceStatus";
import { StatusSummaryType } from "./types";

interface DeviceStatusProps {
  isInnerCard?: boolean;
  hasCardWrapper?: boolean;
  memoiseDevicesData?: boolean;
  setShowBackButton?: Dispatch<SetStateAction<boolean>>;
  setBackButton?: Dispatch<SetStateAction<Element | null>>;
  activeTab?: CONNECTIVITY_TABS;
}

const DeviceStatus = ({
  isInnerCard,
  hasCardWrapper = true,
  memoiseDevicesData,
  setShowBackButton,
  setBackButton,
  activeTab = CONNECTIVITY_TABS.Devices
}: DeviceStatusProps) => {
  const { setDashboardWidgets } = useDashboardContext();

  const { isLoading: isDeviceStatusLoading } =
    useDeviceStatus(memoiseDevicesData);

  const isDesktopDrawerOpen = useDrawerStore(
    state => state.isDesktopDrawerOpen
  );

  const { isDashboardLoading, deviceStatus, deviceStatusError } =
    useDashboardStore();

  const { toSm, to1600, fromLg } = useMediaQueries();

  const { t } = useTranslation();

  const isChangeSelectedCompany = useChangeSelectedCompanyStore(
    state => state.isChangeSelectedCompany
  );

  const [hovered, setHovered] = useState<boolean>(false);
  const [hoveredElement, setHoveredElement] = useState<number | null>(null);
  const [hoveredListItem, setHoveredListItem] = useState<string | null>(null);
  const [showSingleStatusInfo, setShowSingleStatusInfo] =
    useState<StatusSummaryType | null>(null);

  const { generateStatusList, generateBackButton, generateStatusContent } =
    useChartElements({
      t
    });

  // display column height only when desktop drawer is open on 1440px to 1600px screens
  const enlargeListHeight = isDesktopDrawerOpen && to1600 && fromLg;

  const { chartListBox, chartContainer } = dashboardStyles({
    toSm,
    fixedContentHeight: true,
    enlargeListHeight: hasCardWrapper && enlargeListHeight
  });
  const isStatusLoading = isDeviceStatusLoading && !memoiseDevicesData;
  const isLoading = isDashboardLoading || isStatusLoading;

  useEffect(() => {
    // set back button when single status info is shown in Connectivity card
    if (!hasCardWrapper) {
      setShowBackButton?.(Boolean(showSingleStatusInfo));

      Boolean(showSingleStatusInfo) &&
        setBackButton?.(generateBackButton(handleGoBack) as any);
    }

    !hovered && setHoveredElement(null);
  }, [
    hovered,
    hoveredElement,
    showSingleStatusInfo,
    deviceStatus,
    setShowBackButton,
    setBackButton,
    generateBackButton,
    hasCardWrapper
  ]);

  useEffect(() => {
    setDashboardWidgets(prevWidgets => ({
      ...prevWidgets,
      showDeviceStatus: true,
      showCompanyCards: hasCardWrapper // hide company cards status when device status is shown
    }));
    // close single status info when loading
    isLoading && setShowSingleStatusInfo(null);
  }, [isLoading, setDashboardWidgets, hasCardWrapper]);

  useEffect(() => {
    isChangeSelectedCompany && handleGoBack();
  }, [isChangeSelectedCompany]);

  const generateList = (deviceStatus: any) =>
    generateStatusList({
      statusData: deviceStatus,
      hoveredElement,
      hovered,
      hoveredListItem,
      setShowSingleStatusInfo,
      setHoveredListItem
    });

  const showSingleChartItemInfo =
    deviceStatus && showSingleStatusInfo && !isLoading;
  const showListItemsInfo = deviceStatus && !isLoading;

  const handleGoBack = () => {
    setShowSingleStatusInfo(null);
    setHoveredListItem(null);
    setHoveredElement(null);
    setHovered(false);
  };

  const statusContent = generateStatusContent({
    isLoading,
    showSingleChartItemInfo,
    showListItemsInfo,
    chartContainer,
    statusData: deviceStatus,
    showSingleStatusInfo,
    hasCardWrapper,
    setHovered,
    setHoveredElement,
    setShowSingleStatusInfo,
    chartListBox,
    generateList,
    activeTab
  });

  return (
    <>
      {hasCardWrapper ? (
        <CardWrapper
          // height setting on toSm screen is not needed here
          isTopSection={!toSm && !isInnerCard}
          cardTitle={t("Dashboard##device status")}
          isLoading={isLoading}
          titleInlineAction={
            showSingleStatusInfo && generateBackButton(handleGoBack)
          }
          isError={deviceStatusError}
          isInnerCard={isInnerCard}
        >
          {statusContent}
        </CardWrapper>
      ) : (
        statusContent
      )}
    </>
  );
};

export default React.memo(DeviceStatus);
